import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from '@/contexts/AuthContext';

export interface UserNumerologyData {
  destino: number | null;
  missao: number | null;
  expressao: number | null;
  nomeCompleto: string | null;
  isLoading: boolean;
  error: string | null;
  hasData: boolean;
}

export function useUserNumerologyNumbers() {
  const { user } = useAuthContext();
  const [data, setData] = useState<UserNumerologyData>({
    destino: null,
    missao: null,
    expressao: null,
    nomeCompleto: null,
    isLoading: true,
    error: null,
    hasData: false
  });

  const fetchNumerologyData = async () => {
    if (!user) {
      setData(prev => ({ ...prev, isLoading: false }));
      return;
    }

    try {
      setData(prev => ({ ...prev, isLoading: true, error: null }));

      // Buscar mapas do usuário autenticado
      if (!user?.id) {
        console.warn('User ID não disponível para buscar mapas');
        setData({
          destino: null,
          missao: null,
          expressao: null,
          nomeCompleto: null,
          isLoading: false,
          error: null,
          hasData: false
        });
        return;
      }

      // Buscar o primeiro mapa numerológico do usuário com seus cálculos
      const { data: mapaData, error } = await supabase
        .from('mapas_numerologicos')
        .select(`
          nome_completo_nascimento,
          calculos_numerologicos (
            destino,
            missao,
            expressao
          )
        `)
        .eq('usuario_id', user.id)
        .order('created_at', { ascending: true })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Erro ao buscar dados numerológicos:', error);
        throw new Error('Erro ao buscar dados numerológicos');
      }

      if (mapaData && mapaData.calculos_numerologicos) {
        const calculo = Array.isArray(mapaData.calculos_numerologicos) 
          ? mapaData.calculos_numerologicos[0] 
          : mapaData.calculos_numerologicos;

        setData({
          destino: calculo?.destino || null,
          missao: calculo?.missao || null,
          expressao: calculo?.expressao || null,
          nomeCompleto: mapaData.nome_completo_nascimento,
          isLoading: false,
          error: null,
          hasData: true
        });
      } else {
        // Nenhum mapa encontrado
        setData({
          destino: null,
          missao: null,
          expressao: null,
          nomeCompleto: null,
          isLoading: false,
          error: null,
          hasData: false
        });
      }

    } catch (error) {
      console.error('Erro no hook de números numerológicos:', error);
      setData({
        destino: null,
        missao: null,
        expressao: null,
        nomeCompleto: null,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        hasData: false
      });
    }
  };

  useEffect(() => {
    fetchNumerologyData();
  }, [user]);

  const refetch = () => {
    fetchNumerologyData();
  };

  return {
    ...data,
    refetch
  };
}